import * as React from 'react'

import { observer } from 'mobx-react'

import MapViewSetUpStore from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'
import { EquipmentResourceEditBody } from '~/client/src/shared/components/EquipmentResourceEdit/EquipmentResourceEditBody'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { useResolvedTemplate } from '~/client/src/shared/components/FormListSelector/useResolvedTemplate'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import {
  LinkedResourceEdit,
  useLinkedResourceEditor,
} from '~/client/src/shared/components/ResourceLink'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { FORBIDDEN_SITE_NAME } from '~/client/src/shared/constants/locationName'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  compactResourceData,
  mergeResourceData,
  splitResourceFormData,
} from '~/client/src/shared/hooks/splitResourceFormData'
import useEquipmentResources from '~/client/src/shared/hooks/useEquipmentResources'
import { useEquipmentSensorLinkField } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IGeoPosition from '~/client/src/shared/models/IGeoPosition'
import OffloadingEquipment from '~/client/src/shared/models/LocationObjects/OffloadingEquipment'

import ProjectSetUpPageStore from '../../views/ProjectSetUp/ProjectSetUpPage.store'
import { buildMapPropertiesSection } from './buildMapPropertiesSection'
import { MapResourceFieldSync } from './MapResourceFieldSync'

interface IProps {
  store: MapViewSetUpStore
  projectSetUpPageStore: ProjectSetUpPageStore
  viewport: IGeoPosition
  saveAlignment: () => void
  exitRubber: () => void
  projectId?: string
}

/**
 * The Equipment resource card rendered on the Maps setup view in place of
 * PropertiesPanel when the selected map object is an equipment — for both an
 * existing equipment (edit) and a freshly-drawn one (create). Record fields save
 * via the equipment mutation; the injected "Map Properties" section reuses
 * PropertiesPanel (bound to the live map store) for the per-map display props.
 */
const MapEquipmentResourceCard: React.FC<IProps> = ({
  store,
  projectSetUpPageStore,
  viewport,
  saveAlignment,
  exitRubber,
  projectId,
}) => {
  const runtimeContext = useResourceRuntimeContext()
  const { records, save, schema, controlCommands } = useEquipmentResources({
    projectId,
  })

  const dataObjectId =
    store.mapViewItemsSetupStore.selectedMapViewItem?.dataObject?.id
  const recordId = dataObjectId ? String(dataObjectId) : null
  // A drawn-but-unsaved equipment has no backend id yet, so no resource record
  // exists — render the card in creation mode instead of editing an existing one.
  const isCreating = !recordId

  const [chosenTemplateId, setChosenTemplateId] = React.useState<string | null>(
    null,
  )
  const {
    effectiveTemplateId,
    templates,
    loading: templatesLoading,
  } = useResolvedTemplate({
    enabled: isCreating,
    chosenTemplateId,
    templates: schema.templates,
    loading: schema.loading,
  })

  const isAdmin = projectSetUpPageStore.isAdmin
  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)

  // IResourceRecord is structurally a ResourceEditRecord, so it's passed through
  // directly — the card reads it as the form's backing record.
  const record = React.useMemo(
    () => (recordId ? (records.find(r => r.id === recordId) ?? null) : null),
    [records, recordId],
  )

  const drawnItem = store.mapViewItemsSetupStore.selectedMapViewItem

  // Seed the creation form's root fields from the drawn placement so the name and
  // icon (and color/parent) it was drawn with are pre-filled — the name being the
  // one MapViewItemFactory derived from the creation icon ("Crane"), so the form
  // agrees with the label the map is already showing.
  //
  // Keyed on the selected item, not on mount: every unsaved item shares the card's
  // React key (`id ?? 'new-equipment'`), so a second drawn item can reuse this
  // instance and would otherwise keep the first one's seeds. Selection reassigns a
  // fresh copy, so the identity changes exactly when the drawn item does.
  const creationRootDefaults = React.useMemo(() => {
    if (!isCreating) return undefined
    const drawn = drawnItem?.dataObject
    return {
      name: drawn?.name ?? null,
      iconName: drawn?.iconName ?? null,
      color: drawn?.color ?? null,
      parent: drawn?.parent ?? null,
    }
  }, [isCreating, drawnItem])

  const linkField = useEquipmentSensorLinkField({
    projectId,
    sourceRecordId: recordId,
    sourceRecord: record,
  })

  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  // Close the resource card like any editor: deselect without the map's
  // "Confirm changes?" dialog. The footer Save is the explicit save path; X and
  // Cancel discard uncommitted edits — the same outcome as that dialog's Cancel
  // (hideMapViewItemSaveConfirmDialog also just deselects).
  const handleClose = React.useCallback(() => {
    store.mapViewItemsSetupStore.deselectMapViewItem()
  }, [store])

  // Delete via the map's existing confirmation flow, which soft-deletes the
  // equipment resource and removes its map placement (rendered by GlobeProperties).
  const handleDelete = React.useCallback(() => {
    const item = store.mapViewItemsSetupStore.selectedMapViewItem
    if (item) store.mapViewItemsSetupStore.showDeleteConfirmationDialog(item)
  }, [store])

  const handleSave = React.useCallback(
    async (formData: Record<string, any>) => {
      // Capture the selected item up front and bail if it no longer matches this
      // card's record (async gap / user reselected another map object), so form
      // data is never applied to or saved onto the wrong item.
      const item = store.mapViewItemsSetupStore.selectedMapViewItem
      if (!item?.dataObject || String(item.dataObject.id) !== recordId) {
        throw new Error('Map selection changed before save')
      }

      const { rootFields, dataFields } = splitResourceFormData(
        ResourceTypeId.OFFLOADING_EQUIPMENT,
        formData,
        ['operatingIntervals', 'permittedCompanies'],
      )
      const equipment = item.dataObject as OffloadingEquipment

      // Validate the incoming name and the item's placement WITHOUT mutating the
      // live map object, so a name the map layer rejects (reserved "Site"), an
      // invalid placement, a failed equipment mutation, or a selection change
      // never leaves uncommitted edits on the MobX map item.
      const newName =
        typeof rootFields.name === 'string' ? rootFields.name : item.name
      if (
        !newName ||
        newName === FORBIDDEN_SITE_NAME ||
        !item.isValid(store.isGlobeMode)
      ) {
        throw new Error('Invalid map properties')
      }

      // Persist the resource form fields via the equipment mutation. ResourceEdit
      // already normalized formData and only closes the card after onSave
      // resolves, so a throw here keeps the user's edits and the panel open.
      await save(recordId, formData)

      // Selection may have changed during the async save — don't persist onto a
      // different item.
      if (store.mapViewItemsSetupStore.selectedMapViewItem !== item) {
        throw new Error('Map selection changed during save')
      }

      // The equipment mutation has now committed name/color/icon/parent; only at
      // this point sync them onto the live map item for its own save (icon color,
      // per-map display, dataObject). A parent change recomputes accessible
      // levels. Same clear/delete semantics as the resource save.
      item.setName(newName)
      equipment.data = mergeResourceData(equipment.data ?? {}, dataFields)
      // The form's single `color` field is the ICON color only — set just that
      // with setColor, leaving the shape fill/line colors (edited independently
      // on the Map tab) untouched. A blanket setAllColors here would repaint the
      // shape and clobber those independent edits (the reported regression).
      if (rootFields.color && rootFields.color !== item.color) {
        item.setColor(rootFields.color)
      }
      if ('iconName' in rootFields) {
        equipment.iconName = rootFields.iconName ?? equipment.iconName
      }
      if ('parent' in rootFields) {
        item.setParent(rootFields.parent ?? null)
        store.mapViewItemsSetupStore.resetAccessibleLevels(item)
      }

      // Persist the map object (color/icon/parent/shape + per-map display).
      // Throw on failure so ResourceEdit keeps the card open (matching
      // PropertiesPanel) instead of closing and losing the map-property edits.
      // skipDataObjectSave: the resource + controls mutations above already
      // persisted the equipment record. Re-saving the location DTO from the map
      // path would write the stale MobX control fields (e.g. permitted companies,
      // operating intervals) back over the just-saved controls — so save only the
      // sitemap placement here.
      const ok = await store.mapViewItemsSetupStore.saveSelectedMapViewItem(
        false,
        item.isDisplayed,
        true,
      )
      if (!ok) {
        throw new Error('Failed to save equipment map properties')
      }
    },
    [store, save, recordId],
  )

  // Create a freshly-drawn equipment. All resource fields are applied onto the
  // live (unsaved) map dataObject, then persisted in a SINGLE write via the map
  // save path — which serializes name/icon/color/parent/data/templateId through
  // SaveEquipments and writes the new id back onto the map object. Creating via
  // the resource mutation instead would double-write: the MobX map store can't
  // see the Apollo-created record and would create the equipment again.
  const handleCreate = React.useCallback(
    async (formData: Record<string, any>) => {
      const item = store.mapViewItemsSetupStore.selectedMapViewItem
      // Must still be the unsaved drawn item (no backend id yet).
      if (!item?.dataObject || item.dataObject.id) {
        throw new Error('Map selection changed before create')
      }

      const { templateId, rootFields, dataFields } = splitResourceFormData(
        ResourceTypeId.OFFLOADING_EQUIPMENT,
        formData,
      )
      const equipment = item.dataObject as OffloadingEquipment

      const newName =
        typeof rootFields.name === 'string' ? rootFields.name : item.name
      if (!newName || newName === FORBIDDEN_SITE_NAME) {
        throw new Error('Invalid map properties')
      }

      item.setName(newName)
      equipment.data = compactResourceData(dataFields)
      if (rootFields.color) {
        item.setAllColors(rootFields.color)
      }
      if ('iconName' in rootFields && rootFields.iconName) {
        equipment.iconName = rootFields.iconName
      }
      if ('parent' in rootFields) {
        item.setParent(rootFields.parent ?? null)
        store.mapViewItemsSetupStore.resetAccessibleLevels(item)
      }
      equipment.templateId = templateId ?? effectiveTemplateId ?? null

      if (!item.isValid(store.isGlobeMode)) {
        throw new Error('Invalid map properties')
      }

      // shouldKeepSelected: keep the freshly-created equipment selected so the
      // card stays open in edit mode (see saveSelectedMapViewItem for the fallback).
      const ok = await store.mapViewItemsSetupStore.saveSelectedMapViewItem(
        true,
        item.isDisplayed,
      )
      if (!ok) {
        throw new Error('Failed to create equipment')
      }
    },
    [store, effectiveTemplateId],
  )

  // Creation: wait for templates, then pick a form when there's more than one.
  if (isCreating && templatesLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
        <Spinner size="md" />
      </div>
    )
  }
  if (isCreating && !effectiveTemplateId) {
    if (isCreatingNewForm) {
      return (
        <FormPropertiesSidebar
          mode="create"
          resourceTypeId={ResourceTypeId.OFFLOADING_EQUIPMENT}
          onClose={handleClose}
          onBackToPicker={() => setIsCreatingNewForm(false)}
          resourceSchema={schema}
          onCreated={newTemplateId => {
            setIsCreatingNewForm(false)
            setChosenTemplateId(newTemplateId)
          }}
        />
      )
    }
    return (
      <FormListSelector
        title={Localization.translator.selectEquipmentForm}
        titleVariant="panel"
        resourceTypeId={ResourceTypeId.OFFLOADING_EQUIPMENT}
        onSelect={setChosenTemplateId}
        onClose={handleClose}
        onCreateNewForm={isAdmin ? () => setIsCreatingNewForm(true) : undefined}
        templates={templates}
        loading={schema.loading}
      />
    )
  }
  // Edit mode's record comes from the project resource store and can lag a beat
  // behind map selection immediately after creation. Show a spinner rather than
  // a blank panel while the shared location event catches up.
  if (!isCreating && !record) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
        <Spinner size="md" />
      </div>
    )
  }

  const qrCodeUrl = isCreating
    ? null
    : `${window.location.origin}/resource/${ResourceTypeId.OFFLOADING_EQUIPMENT}/${recordId}`

  const mapPropertiesSection = buildMapPropertiesSection({
    store,
    projectSetUpPageStore,
    viewport,
    saveAlignment,
    exitRubber,
  })

  return (
    <>
      <EquipmentResourceEditBody
        recordId={recordId ?? 'new'}
        record={isCreating ? null : record}
        templateId={isCreating ? effectiveTemplateId || undefined : undefined}
        resourceTypeId={ResourceTypeId.OFFLOADING_EQUIPMENT}
        runtimeContext={runtimeContext}
        projectId={projectId}
        qrCodeUrl={qrCodeUrl}
        isCreationMode={isCreating}
        linkField={linkField}
        onClose={handleClose}
        onSave={isCreating ? handleCreate : handleSave}
        onTemplateChange={isCreating ? setChosenTemplateId : undefined}
        formSelector={
          isCreating ? (
            <ResourceEdit.FormSelector
              resourceTypeId={ResourceTypeId.OFFLOADING_EQUIPMENT}
              templates={templates}
              currentTemplateId={effectiveTemplateId}
            />
          ) : undefined
        }
        creationRootDefaults={creationRootDefaults}
        isMapsSurface
        keepOpenOnCreate
        onDelete={handleDelete}
        defaultExpandedSectionId="map"
        resourceSchema={schema}
        onOpenLinked={openLinkedRecord}
        extraSections={[mapPropertiesSection]}
        formSync={<MapResourceFieldSync store={store} />}
        controlCommands={controlCommands}
        controlFields={
          isCreating ? undefined : extractControlFields(record?.root ?? {})
        }
      />
      <LinkedResourceEdit
        open={!!openedLinkedRecordId}
        recordId={openedLinkedRecordId}
        projectId={projectId}
        resourceTypeId={linkField.targetResourceTypeId}
        onClose={closeLinkedRecord}
        shell={children => <SidePanel zIndex={110}>{children}</SidePanel>}
      />
    </>
  )
}

export default observer(MapEquipmentResourceCard)
